/**
 * Virtualized Table Body Component
 *
 * Renders virtualized table body for better performance with large datasets.
 * Uses spacer rows (instead of absolute positioning) to keep cells in normal
 * table-layout flow, ensuring header/body column alignment.
 */
import { flexRender } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { cn } from "@/shared/lib/utils";
import { mergeRowProps } from "../utils";
import { CopyableCell } from "./CopyableCell";
import { EmptyState, LoadingSkeleton } from "./TableStates";
import { TruncateWrapper } from "./TruncateWrapper";
import type { AppCell } from "../features";
import type { TableBodyProps, TanStackColumn } from "../types";
import type { RowData } from "@tanstack/react-table";

export const VirtualizedTableBody = <T extends RowData>({
  hasNoVisibleColumns,
  columns,
  isLoading,
  data,
  isLoadingMore,
  skeletonRowCount,
  visibleColumns,
  table,
  onRowClick,
  getRowClassName,
  isHighlightedRow,
  highlightedRowId,
  tooltipClassName,
  virtualRowHeight = 52,
  virtualOverscan = 5,
  fetchNextPage,
  hasNextPage,
  stickyHeader,
  getRowProps,
}: TableBodyProps<T>) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const rows = table.getRowModel().rows;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => virtualRowHeight,
    overscan: virtualOverscan,
  });

  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || isLoading || isLoadingMore) {
      return;
    }

    const trigger = loadMoreTriggerRef.current;
    if (!trigger) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          fetchNextPage().catch(() => {});
        }
      },
      {
        root: tableContainerRef.current,
        rootMargin: "200px 0px 200px 0px",
        threshold: 0,
      },
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isLoading, isLoadingMore, rows.length]);

  const colCount = visibleColumns.length || columns.length;

  const renderBody = () => {
    if (hasNoVisibleColumns && !isLoading) {
      return (
        <tbody className="bg-white divide-y divide-gray-200">
          <EmptyState colSpan={columns.length} />
        </tbody>
      );
    }

    if (!hasNoVisibleColumns && isLoading && (data.length === 0 || !isLoadingMore)) {
      return (
        <tbody className="bg-white divide-y divide-gray-200">
          <LoadingSkeleton colSpan={colCount} rowCount={skeletonRowCount} />
        </tbody>
      );
    }

    const virtualItems = virtualizer.getVirtualItems();
    const paddingTop = virtualItems[0]?.start ?? 0;
    const lastItem = virtualItems[virtualItems.length - 1];
    const paddingBottom = lastItem
      ? virtualizer.getTotalSize() - lastItem.end
      : 0;

    return (
      <tbody className="bg-white divide-y divide-gray-200">
        {paddingTop > 0 && (
          <tr>
            <td
              style={{ height: paddingTop, padding: 0, border: 0 }}
              colSpan={colCount}
            />
          </tr>
        )}
        {virtualItems.map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (!row) return null;

          const { rowClassName, restRowProps, onClick } = mergeRowProps(getRowProps, row.original, onRowClick);
          return (
            <tr
              key={row.id}
              {...restRowProps}
              className={cn(
                "transition-colors cursor-pointer",
                getRowClassName
                  ? getRowClassName(row.original)
                  : isHighlightedRow?.(row.original, highlightedRowId)
                    ? "bg-gray-100 hover:bg-gray-200"
                    : "hover:bg-gray-100",
                rowClassName,
              )}
              onClick={onClick}
            >
              {row.getVisibleCells().map((cell: AppCell<T>) => {
                const column = cell.column.columnDef as TanStackColumn<T>;
                return (
                  <td
                    key={cell.id}
                    className={cn(
                      "px-6 py-4 whitespace-nowrap cursor-pointer",
                      column.columnClassName,
                      column.isSelectionColumn && "!px-2 !py-2",
                      column.isCopyable && "!cursor-copy",
                    )}
                    onClick={async (e) => {
                      if (column.isSelectionColumn || column.isCopyable) {
                        e.stopPropagation();
                      }
                      if (column.isCopyable) {
                        const cellValue = cell.getValue();
                        if (cellValue !== null) {
                          try {
                            await navigator.clipboard.writeText(String(cellValue));
                            toast.success("ID copié dans le presse-papier");
                          }
                          catch {
                            toast.error("Erreur lors de la copie");
                          }
                        }
                      }
                    }}
                  >
                    {column.isSelectionColumn
                      ? (
                        <div
                          className="h-full w-full flex items-center justify-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      )
                      : column.isCopyable
                        ? (
                          <TruncateWrapper tooltipClassName={tooltipClassName}>
                            <CopyableCell>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </CopyableCell>
                          </TruncateWrapper>
                        )
                        : column.disableTruncate
                          ? (
                            <div className="cursor-pointer w-fit min-w-max">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </div>
                          )
                          : (
                            <TruncateWrapper tooltipClassName={tooltipClassName}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TruncateWrapper>
                          )}
                  </td>
                );
              })}
            </tr>
          );
        })}
        {paddingBottom > 0 && (
          <tr>
            <td
              style={{ height: paddingBottom, padding: 0, border: 0 }}
              colSpan={colCount}
            />
          </tr>
        )}
      </tbody>
    );
  };

  return (
    <div
      ref={tableContainerRef}
      className="overflow-auto flex-1"
      style={{ maxHeight: "calc(100vh - 200px)" }}
    >
      <table
        className="min-w-full divide-y divide-gray-200"
        style={{
          tableLayout: "fixed",
          width: "100%",
        }}
      >
        <colgroup>
          {table.getVisibleLeafColumns().map((col) => (
            <col key={col.id} style={{ width: `calc(var(--col-${col.id}-size) * 1px)` }} />
          ))}
        </colgroup>
        {stickyHeader}
        {renderBody()}
      </table>
      {isLoadingMore && (
        <div className="p-4">
          <div className="h-4 bg-gray-300 rounded w-full animate-pulse" />
        </div>
      )}
      {hasNextPage && !isLoadingMore && <div ref={loadMoreTriggerRef} className="h-1 w-full" aria-hidden="true" />}
    </div>
  );
};
